import type { ValidationItem } from './types';
import type { UploadResource } from '@/types/upload-intake';
import {
  payloadObject,
  payloadString,
  payloadBool,
} from '@/shared/api/websocket/realtime-sync.payload';

/**
 * Minimal shape every validation source (initial angle resource, poll `getAngle`,
 * WS `validation_completed` payload) exposes. Kept loose so one decoder serves
 * all three call sites — they only differ in how they read the raw fields.
 */
export interface DecodableValidation {
  status?: string;
  recommendations?: string | null;
  routed_to_documents?: boolean;
  routed_to_references?: boolean;
  routed_to_output_style?: boolean;
  validation_failure_reason?: string | null;
  can_override_building?: boolean;
  is_prohibited?: boolean;
  is_technical_error?: boolean;
  auto_accepted_as_view?: boolean;
}

/**
 * Server-independent copy the row falls back to when the server sent no text
 * of its own. Passed as one object so a new fallback does not thread a new
 * positional argument through the poller, the listener and the reconcile hook.
 */
export interface UploadRowCopy {
  /** The photo itself is unusable. */
  notSuitable: string;
  /** Our side failed. Nothing was charged; the file can be sent again. */
  technicalError: string;
}

export interface DecodedValidation {
  status: ValidationItem['status'];
  note?: string;
  error?: string;
  validation?: ValidationItem['validation'];
}

/**
 * Single source of truth that maps a finished validation meta to a
 * {@link ValidationItem} status + fields. Used by the initial-items mapping, the
 * polling hook, and the WS listener so all three stay in lockstep.
 *
 * Intake-mode split (post-`intent:'view'` backend):
 * - LENIENT (Brief "Add files", no intent): a `different_building` photo is
 *   AUTO-ROUTED to References (`routed_to_references=true`) and the angle is
 *   soft-deleted → `added_as_reference` (accepted; single "Use as a view"
 *   action). All other routed outcomes (drawing→documents, inspiration→
 *   references, artistic→output style) are unchanged neutral "moved" rows.
 * - STRICT (concept "add view", `intent:'view'`): the server does NOT route a
 *   `different_building` (`routed_to_references=false`) → `different_building_block`
 *   (hard block; only "It's my building"). Every other non-valid outcome is a
 *   plain `invalid` reject row.
 *
 * Branch order matters: the output-style and different-building checks run BEFORE
 * the generic `routed_to_references` check because the server sets
 * `routed_to_references` alongside the more specific flags.
 */
export function decodeValidation(
  v: DecodableValidation,
  copy: UploadRowCopy
): DecodedValidation {
  const status = v.status ?? '';
  const recommendations = v.recommendations ?? undefined;
  const isValid = status === 'valid';
  const isTechnicalError =
    status === 'technical_error' || v.is_technical_error === true;
  const isProhibited = status === 'prohibited' || v.is_prohibited === true;

  // Our side failed, not the photo. ADR-002 makes this the one outcome that is
  // repeated for free, so it must not read as "your photo was rejected" — and
  // it must not slip through as a success either, which is how the window used
  // to close on it without a word.
  if (isTechnicalError) {
    return {
      status: 'error',
      error: recommendations || copy.technicalError,
      validation: {
        is_valid: false,
        is_technical_error: true,
        recommendations,
      },
    };
  }

  const isDifferentBuilding = status === 'different_building';
  const routedToReferences =
    status === 'reference' || v.routed_to_references === true;

  // STRICT different-building hard block: flagged different_building but NOT
  // routed. Only "It's my building" is offered (gated on can_override_building).
  if (isDifferentBuilding && !routedToReferences) {
    return {
      status: 'different_building_block',
      note: recommendations,
      validation: {
        validation_failure_reason:
          v.validation_failure_reason ?? 'different_building',
        can_override_building: v.can_override_building === true,
        recommendations,
      },
    };
  }

  // LENIENT different-building: auto-routed to References as a plain reference.
  // Accepted, with a single "Use as a view" forward action.
  if (isDifferentBuilding && routedToReferences) {
    return {
      status: 'added_as_reference',
      note: recommendations,
      validation: {
        validation_failure_reason:
          v.validation_failure_reason ?? 'different_building',
        can_override_building: v.can_override_building === true,
        recommendations,
      },
    };
  }

  // Every routed outcome below can be promoted back to a view in one tap; carry
  // the server's can_override_building flag so ValidationItemsList can offer it.
  const canOverrideBuilding = v.can_override_building === true;

  // Drawing/document → Brief documents.
  const routedToDocuments =
    status === 'info_source' || v.routed_to_documents === true;
  if (routedToDocuments) {
    return {
      status: 'moved_to_documents',
      note: recommendations,
      validation: { can_override_building: canOverrideBuilding, recommendations },
    };
  }

  // Artistic/illustrative rendering → References as an output-style reference.
  // Checked before the generic references branch (server sets both flags).
  const routedToOutputStyle =
    status === 'output_style' || v.routed_to_output_style === true;
  if (routedToOutputStyle) {
    return {
      status: 'moved_to_output_style',
      note: recommendations,
      validation: { can_override_building: canOverrideBuilding, recommendations },
    };
  }

  // Inspiration material → References.
  if (routedToReferences) {
    return {
      status: 'moved_to_references',
      note: recommendations,
      validation: { can_override_building: canOverrideBuilding, recommendations },
    };
  }

  // Auto-accepted view: the classifier filed the FIRST upload of a building as
  // inspiration, but the server kept it as a view by default (status 'valid').
  // Carry the server note so the row explains the call and offers the
  // "Make it a reference" undo action.
  const autoAcceptedAsView = isValid && v.auto_accepted_as_view === true;

  // Valid view, or a plain failure (invalid / prohibited / technical error).
  return {
    status: isValid ? 'valid' : 'invalid',
    note: autoAcceptedAsView ? recommendations : undefined,
    validation: {
      is_valid: isValid,
      is_prohibited: isProhibited,
      is_technical_error: isTechnicalError,
      recommendations,
      validation_failure_reason: v.validation_failure_reason ?? undefined,
      can_override_building: v.can_override_building === true,
      auto_accepted_as_view: autoAcceptedAsView,
    },
    error: !isValid ? recommendations || copy.notSuitable : undefined,
  };
}

/**
 * The settled fields of one intake, read off the `upload` resource.
 *
 * Shared by the polling hook and the reconcile hook so a photo settles the same
 * way whichever of them noticed first. Returns null while the server is still
 * reading it — `is_in_progress` is that answer, and the client keeps no copy of
 * which statuses are terminal.
 */
export function settledUploadPatch(
  upload: UploadResource,
  copy: UploadRowCopy
): Partial<ValidationItem> | null {
  const validation = upload.attributes?.validation;
  if (!validation || validation.is_in_progress !== false) return null;

  const decoded = decodeValidation(
    {
      status: validation.status,
      recommendations: validation.recommendations,
      routed_to_documents: validation.routed_to_documents,
      routed_to_references: validation.routed_to_references,
      routed_to_output_style: validation.routed_to_output_style,
      validation_failure_reason: validation.validation_failure_reason,
      can_override_building: validation.can_override_building,
      auto_accepted_as_view: validation.auto_accepted_as_view,
      is_technical_error: validation.is_technical_error,
    },
    copy
  );

  const outcome = upload.attributes.outcome;

  return {
    status: decoded.status,
    note: decoded.note,
    error: decoded.error,
    validation: decoded.validation,
    angleId: outcome?.type === 'angle' ? (outcome.id ?? undefined) : undefined,
  };
}

/**
 * The settled validation and the view it created, read off an
 * `uploads.completed` socket payload. Null when the payload carries no
 * validation status. Shared by every listener that waits on an intake.
 */
export function readUploadCompletedPayload(
  payload: Parameters<typeof payloadObject>[0]
): { validation: DecodableValidation; angleId: string | undefined } | null {
  const validation = payloadObject(payload, 'validation');
  if (!validation) return null;

  const status = payloadString(validation, 'status');
  if (!status) return null;

  const outcome = payloadObject(payload, 'outcome');
  const outcomeType = outcome ? payloadString(outcome, 'type') : null;
  const angleId =
    outcomeType === 'angle' && outcome
      ? (payloadString(outcome, 'id') ?? undefined)
      : undefined;

  return {
    validation: {
      status,
      recommendations: payloadString(validation, 'recommendations'),
      routed_to_documents:
        payloadBool(validation, 'routed_to_documents') === true,
      routed_to_references:
        payloadBool(validation, 'routed_to_references') === true,
      routed_to_output_style:
        payloadBool(validation, 'routed_to_output_style') === true,
      validation_failure_reason: payloadString(
        validation,
        'validation_failure_reason'
      ),
      can_override_building:
        payloadBool(validation, 'can_override_building') === true,
      auto_accepted_as_view:
        payloadBool(validation, 'auto_accepted_as_view') === true,
    },
    angleId,
  };
}
